import { useEffect, useMemo, useState } from 'react';
import { api, setToken, getToken } from '../lib/auth';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '', remember: true });
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (getToken()) navigate('/', { replace: true });
  }, [navigate]);

  const canSubmit = useMemo(() =>
    form.email.trim().length > 3 && form.password.length >= 6 && !loading, [form, loading]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: form.email.trim(),
        password: form.password,
        remember: form.remember,
      });
      if (!data?.accessToken) throw new Error('No token returned from server');
      setToken(data.accessToken, form.remember); // implement “remember” in your setToken helper if desired
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error) {
      console.error(error);
      setErr(error?.response?.data?.error || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-hero" aria-hidden="true">
        <div className="login-hero-copy">
          <span className="login-hero-eyebrow">TearDrop Hydroponics</span>
          <span className="login-hero-title">Farm OS</span>
        </div>
      </div>
      <main className="login-container">
        <section className="login-card" aria-labelledby="login-title">
          <div className="login-head">
            <img src="https://teardrophydro.co.uk/images/logo/icon.png" alt="" className="login-logo" />
            <h1 id="login-title" className="login-title">Welcome back</h1>
            <p className="login-sub">Sign in to Farm OS</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <label className="field">
              <span className="label">Email</span>
              <input
                className="input"
                type="email"
                name="email"
                inputMode="email"
                autoComplete="username"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
              />
            </label>

            <label className="field">
              <span className="label">Password</span>
              <div className="pw-wrap">
                <input
                  className="input"
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPw(s => !s)}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>

            {err && (
              <p className="error" role="alert" aria-live="assertive">
                {err}
              </p>
            )}

            <button className="btn" type="submit" disabled={!canSubmit}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
